import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const sel = "h-9 w-full rounded-md border border-border bg-background px-2 text-sm";

const ColumnSelect = ({ label, value, onChange, columns, testId, optional }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <select className={sel} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} data-testid={testId}>
      <option value="">{optional ? "None" : "Choose a column"}</option>
      {columns.map((c, i) => <option key={i} value={i}>{c || `Column ${i + 1}`}</option>)}
    </select>
  </div>
);

export default function ColumnMapDialog({ info, busy, onClose, onSubmit }) {
  const s = info.suggested;
  const [m, setM] = useState({ ...s, split: s.amount == null && (s.money_in != null || s.money_out != null) });
  const set = (k) => (v) => setM((x) => ({ ...x, [k]: v }));
  const ready = m.account.trim() && m.date != null && m.description != null && (m.split ? m.money_in != null || m.money_out != null : m.amount != null);
  const submit = () => {
    const { split, kind, ...rest } = m;
    onSubmit({ ...rest, amount: split ? null : m.amount, money_in: split ? m.money_in : null, money_out: split ? m.money_out : null });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="column-map-dialog">
        <DialogHeader>
          <DialogTitle>Which columns are which?</DialogTitle>
          <DialogDescription>We haven't seen a file laid out like {info.filename} before. Match the columns once and the next file from this bank is read automatically.</DialogDescription>
        </DialogHeader>
        <div className="overflow-x-auto border border-border rounded-md">
          <table className="text-xs w-full" data-testid="column-map-sample">
            <thead><tr className="bg-muted/40">{info.columns.map((c, i) => <th key={i} className="px-2 py-1.5 text-left font-medium whitespace-nowrap">{c || `Column ${i + 1}`}</th>)}</tr></thead>
            <tbody>{info.sample.map((r, i) => <tr key={i} className="border-t border-border">{info.columns.map((_, j) => <td key={j} className="px-2 py-1 whitespace-nowrap font-mono">{r[j]}</td>)}</tr>)}</tbody>
          </table>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="map-account">Account name</Label>
            <Input id="map-account" value={m.account} onChange={(e) => set("account")(e.target.value)} placeholder="e.g. Westpac business, NAB Visa" maxLength={40} data-testid="map-account-input" />
          </div>
          <ColumnSelect label="Date" value={m.date} onChange={set("date")} columns={info.columns} testId="map-date-select" />
          <div className="space-y-1.5">
            <Label>Date written as</Label>
            <select className={sel} value={m.date_order} onChange={(e) => set("date_order")(e.target.value)} data-testid="map-date-order-select">
              <option value="dmy">Day / month / year</option>
              <option value="mdy">Month / day / year</option>
              <option value="ymd">Year - month - day</option>
            </select>
          </div>
          <ColumnSelect label="Description" value={m.description} onChange={set("description")} columns={info.columns} testId="map-description-select" />
          <div className="space-y-1.5">
            <Label>Amounts are in</Label>
            <select className={sel} value={m.split ? "split" : "one"} onChange={(e) => set("split")(e.target.value === "split")} data-testid="map-amount-mode-select">
              <option value="one">One amount column</option>
              <option value="split">Separate money in and money out columns</option>
            </select>
          </div>
          {m.split ? (
            <>
              <ColumnSelect label="Money in" value={m.money_in} onChange={set("money_in")} columns={info.columns} testId="map-in-select" optional />
              <ColumnSelect label="Money out" value={m.money_out} onChange={set("money_out")} columns={info.columns} testId="map-out-select" optional />
            </>
          ) : (
            <>
              <ColumnSelect label="Amount" value={m.amount} onChange={set("amount")} columns={info.columns} testId="map-amount-select" />
              <div className="space-y-1.5">
                <Label>Money going out shows as</Label>
                <select className={sel} value={m.sign} onChange={(e) => set("sign")(e.target.value)} data-testid="map-sign-select">
                  <option value="neg_out">A minus amount (most bank accounts)</option>
                  <option value="pos_out">A plus amount (most credit cards)</option>
                </select>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="column-map-cancel">Cancel</Button>
          <Button disabled={!ready || busy} onClick={submit} data-testid="column-map-submit">{busy ? "Reading…" : "Read this file"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
